import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { checkFrameQuality } from '../utils/frameQuality';

/**
 * Guided capture of ONE specimen, for identification: up to five photographs.
 *
 *   1. cap      the cap from above
 *   2. gills    the gills (or pores) and the stem, from the side
 *   3. stipe    the stem on its own: ring, surface, attachment
 *   4. base     the stem base: volva, bulb, mycelium
 *   5. habitat  the substrate and surroundings
 *
 * Every step is optional (2026-10-06): a bracket or crust fungus has no stem
 * or gills, and a damaged specimen may have lost them, so a required step made
 * people photograph something else into it -- which the role weights below
 * then trusted as that view. Identifying still needs at least one photograph.
 *
 * Each photograph is sent with its role (view_roles). The role weights --
 * cap 0.45, gills 0.45, stipe 0.20, base 0.15, habitat 0.10, renormalised over
 * the roles present (backend/ai/predict.py SEMANTIC_WEIGHTS) -- are the prior
 * of the attention pooling that fuses the photographs into one embedding per
 * model (backend/ai/view_fusion.py). With only cap, gills and habitat the
 * weights are exactly 0.45 / 0.45 / 0.10.
 *
 * The rules overlay says what makes a photograph identifiable: one specimen,
 * no hands or objects, the natural surface left in place. Every photograph is
 * checked in the browser for skin at the frame's edges and for small round
 * metallic objects (utils/frameQuality.js). Those checks are heuristics, so the
 * warning is gentle and never blocks: the person can always continue.
 *
 * Props:
 *   onComplete({files, roles})  called with the photographs taken, in step order
 *   onCancel()                  optional
 *   analyze(file)               the frame check; injectable for tests
 *   disabled                    while the caller is uploading
 */
export const CAPTURE_STEPS = [
  { role: 'cap', icon: '🍄' },
  { role: 'gills', icon: '🔍' },
  { role: 'stipe', icon: '📏' },
  { role: 'base', icon: '🔻' },
  { role: 'habitat', icon: '🌲' },
];

export const CAPTURE_RULES = [
  { id: 'single', icon: '☝️' },
  { id: 'clean', icon: '✋' },
  { id: 'habitat', icon: '🌿' },
];

// Which rules matter most on each step; the others stay visible, just quieter.
export const RULE_FOCUS = {
  cap: ['single', 'clean'],
  gills: ['single', 'clean'],
  stipe: ['single', 'clean'],
  base: ['clean', 'habitat'],
  habitat: ['habitat', 'clean'],
};

const revoke = (url) => {
  if (url && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(url);
};

export default function GuidedCapture({ onComplete, onCancel, analyze = checkFrameQuality, disabled = false }) {
  const { t } = useTranslation('capture');
  const [index, setIndex] = useState(0);
  const [shots, setShots] = useState({});          // role -> { file, preview, check, token }
  const [rulesOpen, setRulesOpen] = useState(true);
  const shotsRef = useRef(shots);
  shotsRef.current = shots;
  const tokenRef = useRef(0);
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  useEffect(() => () => {
    Object.values(shotsRef.current).forEach(shot => revoke(shot?.preview));
  }, []);

  const step = CAPTURE_STEPS[index];
  const shot = shots[step.role];
  const last = index === CAPTURE_STEPS.length - 1;
  // Any one photograph, of any view, is something to identify.
  const canFinish = CAPTURE_STEPS.some(s => shots[s.role]);

  const choose = async (role, fileList) => {
    const file = Array.from(fileList || []).find(f => (f.type || '').startsWith('image/'));
    if (!file) return;
    tokenRef.current += 1;
    const token = tokenRef.current;
    const preview = typeof URL.createObjectURL === 'function' ? URL.createObjectURL(file) : '';
    setShots(prev => {
      revoke(prev[role]?.preview);
      return { ...prev, [role]: { file, preview, check: 'checking', token } };
    });
    let result = null;
    try {
      result = await analyze(file);
    } catch {
      result = null;
    }
    // A photograph replaced while it was being checked keeps the newer one's result.
    setShots(prev => (prev[role]?.token === token ? { ...prev, [role]: { ...prev[role], check: result } } : prev));
  };

  const remove = (role) => setShots(prev => {
    revoke(prev[role]?.preview);
    const next = { ...prev };
    delete next[role];
    return next;
  });

  const finish = () => {
    const taken = CAPTURE_STEPS.filter(s => shots[s.role]);
    onComplete({ files: taken.map(s => shots[s.role].file), roles: taken.map(s => s.role) });
  };

  const check = shot && shot.check !== 'checking' ? shot.check : null;
  const focus = RULE_FOCUS[step.role];

  return (
    <section data-testid="guided-capture" style={wrapStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
        <h3 style={{ margin: 0, color: 'var(--c-primary-fg)' }}>{t('title')}</h3>
        <span style={mutedStyle}>{t('stepOf', { n: index + 1, total: CAPTURE_STEPS.length })}</span>
      </div>
      <p style={introStyle}>{t('intro')}</p>

      <ol style={progressStyle} aria-label={t('title')}>
        {CAPTURE_STEPS.map((s, i) => (
          <li key={s.role} data-testid={`capture-progress-${s.role}`} data-state={shots[s.role] ? 'taken' : i === index ? 'current' : 'pending'}
              style={progressItem(i === index, Boolean(shots[s.role]))}>
            {s.icon} {t(`steps.${s.role}.title`)}
            <span style={{ ...mutedStyle, marginInlineStart: 4 }}>
              ({shots[s.role] ? t('taken') : t('optional')})
            </span>
          </li>
        ))}
      </ol>

      <div style={{ marginBottom: 10 }}>
        <button type="button" onClick={() => setRulesOpen(o => !o)} title={t('tip.rules')} style={linkButton} aria-expanded={rulesOpen}>
          {rulesOpen ? t('rules.hide') : t('rules.show')}
        </button>
        {rulesOpen && (
          <div role="note" data-testid="capture-rules" aria-label={t('rules.title')} style={rulesGrid}>
            {CAPTURE_RULES.map(rule => {
              const focused = focus.includes(rule.id);
              return (
                <div key={rule.id} data-testid={`capture-rule-${rule.id}`} data-focus={String(focused)} style={ruleCard(focused)}>
                  <strong style={{ display: 'block', marginBottom: 4 }}>{rule.icon} {t(`rules.${rule.id}.title`)}</strong>
                  <div style={{ fontSize: 13 }}>✅ <span style={{ fontWeight: 600 }}>{t('rules.do')}:</span> <span>{t(`rules.${rule.id}.do`)}</span></div>
                  <div style={{ fontSize: 13 }}>🚫 <span style={{ fontWeight: 600 }}>{t('rules.dont')}:</span> <span>{t(`rules.${rule.id}.dont`)}</span></div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div style={stepCard} data-testid={`capture-step-${step.role}`}>
        <h4 style={{ margin: '0 0 4px' }}>{step.icon} {t(`steps.${step.role}.title`)}</h4>
        <p style={{ ...mutedStyle, margin: '0 0 10px' }}>{t(`steps.${step.role}.hint`)}</p>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" onClick={() => cameraRef.current?.click()} disabled={disabled} title={t('tip.camera')} style={btn('var(--c-info)')}>
            📷 {shot ? t('retake') : t('camera')}
          </button>
          <button type="button" onClick={() => galleryRef.current?.click()} disabled={disabled} title={t('tip.gallery')} style={btn('var(--c-primary)')}>
            🗂 {t('gallery')}
          </button>
          <input key={`camera-${step.role}`} ref={cameraRef} type="file" accept="image/*" capture="environment"
                 data-testid="capture-camera-input" title={t('tip.camera')} style={{ display: 'none' }}
                 onChange={e => { choose(step.role, e.target.files); e.target.value = ''; }} />
          <input key={`gallery-${step.role}`} ref={galleryRef} type="file" accept="image/*"
                 data-testid="capture-gallery-input" title={t('tip.gallery')} style={{ display: 'none' }}
                 onChange={e => { choose(step.role, e.target.files); e.target.value = ''; }} />
        </div>

        {shot && (
          <figure style={{ margin: '10px 0 0', position: 'relative', width: 180 }}>
            {shot.preview
              ? <img src={shot.preview} alt={t(`steps.${step.role}.title`)} style={previewStyle} />
              : <div style={{ ...previewStyle, background: 'var(--c-bg-muted)' }} />}
            {!disabled && (
              <button type="button" onClick={() => remove(step.role)} aria-label={t('remove')} title={t('tip.remove')} style={removeStyle}>×</button>
            )}
          </figure>
        )}

        {shot && shot.check === 'checking' && <p style={mutedStyle}>{t('checking')}</p>}
        {check && check.warn && (
          <div role="status" data-testid="capture-warning" style={warningStyle}>
            <strong>{t('warning.foreignObjects')}</strong>
            <ul style={{ margin: '4px 0', paddingInlineStart: 18 }}>
              {check.hand && <li>{t('warning.hand')}</li>}
              {check.foreignObject && <li>{t('warning.coin')}</li>}
            </ul>
            <span style={{ fontSize: 12 }}>{t('warning.nonBlocking')}</span>
          </div>
        )}
        {check && !check.warn && <p data-testid="capture-clear" style={clearStyle}>{t('clear')}</p>}
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
        {onCancel && <button type="button" onClick={onCancel} disabled={disabled} title={t('tip.cancel')} style={btn('var(--c-text-muted)')}>{t('cancel')}</button>}
        {index > 0 && <button type="button" onClick={() => setIndex(i => i - 1)} disabled={disabled} title={t('tip.back')} style={btn('var(--c-text-muted)')}>{t('back')}</button>}
        {!last && (
          <button type="button" onClick={() => setIndex(i => i + 1)} disabled={disabled}
                  title={shot ? t('tip.next') : t('tip.skip')}
                  style={btn(shot ? 'var(--c-primary)' : 'var(--c-text-muted)')}>
            {shot ? t('next') : t('skip')}
          </button>
        )}
        {last && !shot && (
          <button type="button" onClick={finish} disabled={disabled || !canFinish} title={t('tip.skipHabitat')} style={btn('var(--c-text-muted)')}>{t('skipHabitat')}</button>
        )}
        {last && shot && (
          <button type="button" onClick={finish} disabled={disabled || !canFinish} title={t('tip.finish')} style={btn('var(--c-primary)')}>
            {t('finish')}
          </button>
        )}
      </div>
    </section>
  );
}

const wrapStyle = { border: '1px solid var(--c-border)', borderRadius: 8, padding: 14, background: 'var(--c-primary-soft-10)' };
const introStyle = { fontSize: 14, color: 'var(--c-text-body-fg)', margin: '6px 0 10px', lineHeight: 1.5 };
const mutedStyle = { fontSize: 12, color: 'var(--c-text-muted-fg)' };
const progressStyle = { display: 'flex', flexWrap: 'wrap', gap: 6, listStyle: 'none', padding: 0, margin: '0 0 10px' };
const progressItem = (current, taken) => ({
  fontSize: 13, padding: '3px 8px', borderRadius: 12,
  border: `1px solid ${current ? 'var(--c-primary)' : 'var(--c-border-strong)'}`,
  background: taken ? 'var(--c-primary-soft-8)' : current ? 'var(--c-surface)' : 'var(--c-bg-subtle)',
  fontWeight: current ? 600 : 400,
});
const rulesGrid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 8, marginTop: 6 };
const ruleCard = (focused) => ({
  borderRadius: 6, padding: '8px 10px', lineHeight: 1.45,
  border: `1px solid ${focused ? 'var(--c-primary)' : 'var(--c-border)'}`,
  background: focused ? 'var(--c-primary-soft-9)' : 'var(--c-surface)',
  opacity: focused ? 1 : 0.75,
});
const stepCard = { border: '1px solid var(--c-border)', borderRadius: 6, padding: 12, background: 'var(--c-surface)' };
const previewStyle = { width: 180, height: 135, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--c-border)', display: 'block' };
const warningStyle = { marginTop: 10, fontSize: 13, color: 'var(--c-warning-text-fg)', background: 'var(--c-warning-soft)', borderRadius: 6, padding: '8px 12px' };
const clearStyle = { marginTop: 10, fontSize: 13, color: 'var(--c-success-2-fg)' };
const linkButton = { background: 'none', border: 'none', color: 'var(--c-info-fg)', cursor: 'pointer', padding: 0, fontSize: 13 };
const removeStyle = {
  position: 'absolute', top: 2, insetInlineEnd: 2, background: 'rgba(0,0,0,0.55)', color: 'var(--c-on-accent)', border: 'none',
  borderRadius: 4, width: 22, height: 22, cursor: 'pointer', fontSize: 14, lineHeight: '20px', padding: 0,
};
const btn = (bg) => ({ background: bg, color: 'var(--c-on-accent)', border: 'none', borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontSize: 14 });
